import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ════════════════════════════════════════════════════════════════════════
// WEEKLY LOCKOUTS — single-line "sliding cupboard" rail on Home.
//
// One horizontal strip of character portraits; clicking a portrait slides
// that character's raid tiles open INLINE, between them and the next
// portrait. Rallify raids are the source of truth (Locked / Scheduled /
// Bench are read-only); tapping an open tile marks the character saved
// (character_manual_lockout), which excludes them from ARC / MILP / member
// picker / candidates for that instance until Wednesday reset.
// ════════════════════════════════════════════════════════════════════════

type LockState = "locked" | "scheduled" | "bench" | "manual" | "open";

interface LockRaidRef {
  id: string;
  name: string;
  guild_name: string | null;
  starts_at: string;
}

interface LockInstance {
  raid_option_id: number;
  label: string;
  image_url: string | null;
  size: number | null;
  interested: boolean;
  state: LockState;
  raid: LockRaidRef | null;
}

interface LockCharacter {
  id: string;
  name: string;
  class_name: string;
  primary_spec: string | null;
  render_url: string | null;
  raid_count: number;
  /** This character's own reset week. EU and US characters differ. */
  week_start: string;
  week_end: string;
  /** "Wednesday" for EU, "Tuesday" for US. */
  reset_day: string;
  instances: LockInstance[];
}

interface LockoutsPayload {
  week_start: string;
  /** The SOONEST reset among the user's characters, so the countdown never overstates. */
  week_end: string;
  /** Distinct reset days in play. More than one when the account spans regions. */
  reset_days?: string[];
  characters: LockCharacter[];
}

const CLASS_COLORS: Record<string, string> = {
  'Warrior': '#C79C6E',
  'Paladin': '#F58CBA',
  'Hunter': '#ABD473',
  'Rogue': '#FFF569',
  'Priest': '#FFFFFF',
  'Death Knight': '#C41F3B',
  'Shaman': '#0070DE',
  'Mage': '#69CCF0',
  'Warlock': '#9482C9',
  'Monk': '#00FF96',
  'Druid': '#FF7D0A',
  'Demon Hunter': '#A330C9',
  'Evoker': '#33937F',
};

function classColor(className: string): string {
  return CLASS_COLORS[className] || "#94a3b8";
}

function formatCountdown(toMs: number): string {
  const diff = toMs - Date.now();
  if (diff <= 0) return "now";
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m`;
}

// Everything that isn't open counts as "this week is spoken for"
const SAVED_STATES: LockState[] = ["locked", "scheduled", "bench", "manual"];

// Tile / portrait geometry — the cupboard animates max-width from tile count
const PORTRAIT = 64;
const CHARACTER_W = 168;
const TILE_W = 108;

// ── Per-state visual language ───────────────────────────────────────────
// A single source of truth so tiles, icons and the legend all agree.
interface StateMeta {
  label: string;
  accent: string;   // hex — accent bar + glow
  chip: string;     // tailwind text + bg for the corner chip
  border: string;   // tailwind border for the tile
}

const STATE_META: Record<LockState, StateMeta> = {
  locked:    { label: "Locked",    accent: "#F59E0B", chip: "text-amber-800 dark:text-amber-200 bg-amber-500/10 dark:bg-amber-950/70",   border: "border-amber-500/60" },
  manual:    { label: "Saved",     accent: "#FBBF24", chip: "text-amber-800 dark:text-amber-200 bg-amber-500/10 dark:bg-amber-950/70",   border: "border-dashed border-amber-400/60" },
  scheduled: { label: "Signed",    accent: "#22D3EE", chip: "text-cyan-800 dark:text-cyan-200 bg-cyan-500/10 dark:bg-cyan-950/70",     border: "border-cyan-400/50" },
  bench:     { label: "Bench",     accent: "#C084FC", chip: "text-purple-800 dark:text-purple-200 bg-purple-500/10 dark:bg-purple-950/70", border: "border-purple-400/45" },
  open:      { label: "Open",      accent: "#64748B", chip: "text-[var(--c-ink-3)] bg-[var(--c-sunken-2)]",        border: "border-[var(--c-line)] group-hover/tile:border-amber-400/60" },
};

// Compact glyphs shared by tiles + legend
function StateIcon({ state, className = "w-3 h-3" }: { state: LockState; className?: string }) {
  if (state === "scheduled") {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
  }
  if (state === "bench") {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
  }
  if (state === "open") {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>;
  }
  // locked + manual share the padlock
  return <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" /></svg>;
}

export default function WeeklyLockouts({ refreshKey = 0 }: { refreshKey?: number }) {
  const [data, setData] = useState<LockoutsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [, tick] = useState(0);

  const fetchLockouts = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const res = await fetch("/me/lockouts", { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch { /* leave whatever we had */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLockouts(refreshKey > 0); }, [fetchLockouts, refreshKey]);

  // Keep the reset countdown alive
  useEffect(() => {
    const t = setInterval(() => tick(v => v + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Most-raided character front of the rail
  const characters = useMemo(() => {
    const list = [...(data?.characters || [])];
    list.sort((a, b) => b.raid_count - a.raid_count || a.name.localeCompare(b.name));
    return list;
  }, [data]);

  // One character is always the main card (default: the most-raided one).
  // The others sit beside it as small chips, so every character stays visible
  // however many raids the selected one has.
  useEffect(() => {
    if (characters.length === 0) return;
    if (!selectedId || !characters.some(c => c.id === selectedId)) setSelectedId(characters[0].id);
  }, [characters, selectedId]);
  const selected = characters.find(c => c.id === selectedId) || characters[0] || null;
  const others = characters.filter(c => c.id !== selected?.id);

  // Arrow-button scrolling (native scrollbar is hidden)
  const railRef = useRef<HTMLDivElement | null>(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  const updateScrollState = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanScroll({ left: el.scrollLeft > 4, right: el.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    updateScrollState();
    // The cupboard slide is a 300ms width animation; re-measure after it lands.
    const t = setTimeout(updateScrollState, 350);
    window.addEventListener("resize", updateScrollState);
    return () => { clearTimeout(t); window.removeEventListener("resize", updateScrollState); };
  }, [characters, selectedId, loading, updateScrollState]);

  const scrollRail = (dir: -1 | 1) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, Math.round(el.clientWidth * 0.6)), behavior: "smooth" });
  };

  const setInstanceState = (charId: string, raidOptionId: number, state: LockState) => {
    setData(prev => prev ? {
      ...prev,
      characters: prev.characters.map(c => c.id !== charId ? c : {
        ...c,
        instances: c.instances.map(i => i.raid_option_id !== raidOptionId ? i : { ...i, state }),
      }),
    } : prev);
  };

  const toggleManual = async (char: LockCharacter, inst: LockInstance) => {
    if (inst.state !== "open" && inst.state !== "manual") return;
    const key = `${char.id}:${inst.raid_option_id}`;
    if (pending.has(key)) return;
    const wasOpen = inst.state === "open";
    setPending(prev => new Set(prev).add(key));
    setInstanceState(char.id, inst.raid_option_id, wasOpen ? "manual" : "open");
    try {
      const res = wasOpen
        ? await fetch("/me/lockouts", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ characterId: char.id, raidOptionId: inst.raid_option_id }),
          })
        : await fetch(`/me/lockouts?characterId=${char.id}&raidOptionId=${inst.raid_option_id}`, {
            method: "DELETE",
            credentials: "include",
          });
      if (!res.ok) setInstanceState(char.id, inst.raid_option_id, wasOpen ? "open" : "manual");
    } catch {
      setInstanceState(char.id, inst.raid_option_id, wasOpen ? "open" : "manual");
    }
    setPending(prev => { const n = new Set(prev); n.delete(key); return n; });
  };

  if (!loading && characters.length === 0) return null;

  const resetLabel = data ? formatCountdown(new Date(data.week_end).getTime()) : null;
  // The reset day comes from the server, not a constant: EU resets Wednesday and
  // US resets Tuesday, and an account holding both sees both. The countdown
  // itself is already the soonest of them.
  const resetDayLabel = data?.reset_days?.length ? data.reset_days.join(" / ") : null;

  return (
    <section aria-label="Weekly Lockouts" className="w-full">
      {/* One sleek row: portrait rail + help tooltip. All explanation lives in the tooltip. */}
      {loading ? (
        <div className="flex gap-3 py-1">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-[168px] h-16 rounded-xl bg-[var(--c-sunken)] border border-[var(--c-line-soft)] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <style>{`.wl-rail::-webkit-scrollbar { display: none; }`}</style>
          {selected && (() => {
            const c = selected;
            const color = classColor(c.class_name);
            const savedCount = c.instances.filter(i => SAVED_STATES.includes(i.state)).length;
            const total = c.instances.length;
            const pct = total > 0 ? Math.round((savedCount / total) * 100) : 0;
            // Interested instances lead; sort is stable, so within each group
            // the server's order (admin SORT column) is preserved.
            const tiles = [...c.instances].sort((a, b) => Number(b.interested) - Number(a.interested));
            const isSel = true;
            return (
              <>
                {/* Main card: the selected character. */}
                <div
                  className="flex-shrink-0 flex items-center gap-2.5 rounded-xl border border-amber-500/70 bg-amber-500/[0.06] text-left"
                  style={{ width: CHARACTER_W, height: PORTRAIT, padding: "0 12px 0 8px", boxShadow: "0 4px 14px -8px rgba(245,158,11,0.55)" }}
                  title={`${c.name} · ${[c.primary_spec, c.class_name].filter(Boolean).join(" ")} · ${savedCount}/${total} raids saved`}
                >
                  <div className="relative flex-shrink-0 h-11 w-11 rounded-lg overflow-hidden bg-[var(--c-sunken)]" style={{ boxShadow: "inset 0 0 0 1.5px #F59E0B" }}>
                    {c.render_url ? (
                      <img src={c.render_url} alt={c.name} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg font-black" style={{ color }}>{c.name.charAt(0).toUpperCase()}</div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12px] font-bold leading-tight" style={{ color }}>{c.name}</div>
                    <div className="truncate text-[9px] font-semibold uppercase tracking-wide text-[var(--c-ink-3)] mt-0.5">
                      {[c.primary_spec, c.class_name].filter(Boolean).join(" ") || "\u00A0"}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <div className="h-1 flex-1 rounded-full bg-[var(--c-sunken-2)] overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`text-[9px] font-bold tabular-nums leading-none ${savedCount > 0 ? "text-amber-700/90 dark:text-amber-300/90" : "text-[var(--c-ink-3)]"}`}>{savedCount}/{total}</span>
                    </div>
                  </div>
                </div>

                {/* Other characters: small chips, always visible, two rows high
                    so they sit within the main card's height. */}
                {others.length > 0 && (
                  <div className="flex-shrink-0 grid grid-flow-col grid-rows-2 gap-1" style={{ height: PORTRAIT }}>
                    {others.map(o => {
                      const oc = classColor(o.class_name);
                      const oSaved = o.instances.filter(i => SAVED_STATES.includes(i.state)).length;
                      return (
                        <button
                          key={o.id}
                          onClick={() => setSelectedId(o.id)}
                          title={`${o.name} · ${[o.primary_spec, o.class_name].filter(Boolean).join(" ")} · ${oSaved}/${o.instances.length} raids saved`}
                          aria-label={`Show raids for ${o.name}`}
                          className="relative h-[30px] w-[30px] rounded-lg overflow-hidden bg-[var(--c-sunken)] transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-amber-400/60"
                          style={{ boxShadow: `inset 0 0 0 1.5px ${oc}99` }}
                        >
                          {o.render_url ? (
                            <img src={o.render_url} alt="" className="w-full h-full object-cover object-top" />
                          ) : (
                            <span className="flex h-full w-full items-center justify-center text-[11px] font-black" style={{ color: oc }}>{o.name.charAt(0).toUpperCase()}</span>
                          )}
                          {oSaved > 0 && (
                            <span className="absolute bottom-0 right-0 rounded-tl bg-black/75 px-[3px] text-[8px] font-bold leading-[11px] text-amber-300">{oSaved}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <span className="flex-shrink-0 h-10 w-px bg-[var(--c-line)]" aria-hidden="true" />

                {/* The selected character's raids: the only part that scrolls. */}
                <div
                  ref={railRef}
                  onScroll={updateScrollState}
                  className="wl-rail relative flex items-center gap-1.5 overflow-x-auto py-1 flex-1 min-w-0"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none", height: PORTRAIT + 8 } as React.CSSProperties}
                >
                        {tiles.map(inst => {
                          const meta = STATE_META[inst.state];
                          const editable = inst.state === "open" || inst.state === "manual";
                          const key = `${c.id}:${inst.raid_option_id}`;
                          const isPending = pending.has(key);
                          const isOpen = inst.state === "open";
                          return (
                            <button
                              key={inst.raid_option_id}
                              onClick={() => editable && toggleManual(c, inst)}
                              disabled={isPending || !editable || !isSel}
                              tabIndex={isSel ? 0 : -1}
                              // Text sits on raid artwork, so the tile stays a dark
                              // island in both palettes (see tailwind darkMode).
                              className={`lfm-dark group/tile relative flex-shrink-0 rounded-xl overflow-hidden text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400/50 ${
                                editable ? "cursor-pointer" : "cursor-default"
                              } ${isPending ? "opacity-50" : ""}`}
                              style={{ width: TILE_W, height: PORTRAIT }}
                              title={
                                inst.state === "locked" ? `${inst.label} — locked by "${inst.raid?.name}" (managed in the Planner)` :
                                inst.state === "scheduled" ? `${inst.label} — rostered in "${inst.raid?.name}"` :
                                inst.state === "bench" ? `${inst.label} — on the bench for "${inst.raid?.name}"` :
                                inst.state === "manual" ? `${inst.label} — you marked this saved. Tap to reopen.` :
                                `${inst.label} — open. Tap to mark saved (raided outside Rallify).`
                              }
                            >
                              {/* Artwork */}
                              <div className="absolute inset-0 bg-[var(--c-sunken)]">
                                {inst.image_url && (
                                  <img
                                    src={inst.image_url}
                                    alt=""
                                    className={`w-full h-full object-cover transition-all duration-300 ${
                                      isOpen
                                        ? "grayscale opacity-25 group-hover/tile:opacity-55 group-hover/tile:grayscale-0"
                                        : "opacity-80"
                                    }`}
                                  />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-black/5" />
                              </div>

                              {/* State accent bar (left edge) */}
                              <span
                                className="absolute inset-y-0 left-0 w-[3px] transition-opacity"
                                style={{ background: meta.accent, opacity: isOpen ? 0.35 : 0.9 }}
                              />

                              {/* State chip (top-left) */}
                              <span className={`absolute top-1 left-1.5 inline-flex items-center gap-1 rounded px-1 py-px text-[8px] font-black uppercase tracking-wide backdrop-blur-sm ${
                                isOpen ? "group-hover/tile:text-amber-800 dark:group-hover/tile:text-amber-200 group-hover/tile:bg-amber-950/70" : ""
                              } ${meta.chip}`}>
                                <StateIcon state={inst.state} className="w-2.5 h-2.5" />
                                {isOpen ? <span className="group-hover/tile:hidden">Open</span> : meta.label}
                                {isOpen && <span className="hidden group-hover/tile:inline">Save</span>}
                              </span>

                              {/* Raid label (bottom) */}
                              <div className="absolute bottom-0 inset-x-0 px-1.5 pb-1">
                                <div className={`text-[10px] font-black leading-tight truncate ${
                                  isOpen ? "text-[var(--c-ink-3)] group-hover/tile:text-[var(--c-ink)]" : "text-[var(--c-ink)]"
                                }`}>
                                  {inst.label}
                                </div>
                              </div>

                              {/* Ring on top, not a border underneath. As a
                                  border on the button it sat under artwork that
                                  overflow-hidden clips to the inside of that
                                  same 1px, and the antialiased clip edge made
                                  every tile read blurry. Painted here as the
                                  last child it lands on whole pixels over the
                                  image and stays sharp. */}
                              <span
                                aria-hidden
                                className={`pointer-events-none absolute inset-0 rounded-xl border transition-colors ${meta.border}`}
                              />
                            </button>
                          );
                        })}
                </div>
              </>
            );
          })()}

          {/* Both arrows live to the right of the rail, beside the help dot.
              In the flow on the left they used to shove the first portrait 40px
              inwards the moment the rail overflowed, so the row stopped lining
              up with whatever sits above it. */}
          {(canScroll.left || canScroll.right) && (
            <div className="flex-shrink-0 flex items-center gap-1">
              <button
                onClick={() => scrollRail(-1)}
                disabled={!canScroll.left}
                aria-label="Scroll lockouts left"
                className="h-8 w-8 rounded-full border border-[var(--c-line)] bg-[var(--c-bg-veil)] text-[var(--c-ink-3)] flex items-center justify-center transition-colors hover:enabled:bg-white/[0.08] hover:enabled:text-white disabled:opacity-25 disabled:cursor-default"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button
                onClick={() => scrollRail(1)}
                disabled={!canScroll.right}
                aria-label="Scroll lockouts right"
                className="h-8 w-8 rounded-full border border-[var(--c-line)] bg-[var(--c-bg-veil)] text-[var(--c-ink-3)] flex items-center justify-center transition-colors hover:enabled:bg-white/[0.08] hover:enabled:text-white disabled:opacity-25 disabled:cursor-default"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          )}

          {/* ── Help tooltip (replaces the old header + legend) ──── */}
          <div className="relative group flex-shrink-0">
            <span className="w-5 h-5 rounded-full bg-amber-500/15 text-amber-700/70 dark:text-amber-400/70 text-[10px] font-bold flex items-center justify-center cursor-help hover:bg-amber-500/30 hover:text-amber-700 dark:hover:text-amber-300 transition-colors">?</span>
            <div className="absolute top-full right-0 mt-2 px-4 py-3 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] w-80 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 shadow-xl leading-relaxed">
              <span className="font-bold text-amber-700 dark:text-amber-300">Weekly Lockouts</span>: your characters and their raid saves for this reset week. Click a portrait to slide its raids open.
              <div className="mt-2 text-[var(--c-ink-2)]">
                Locked, Scheduled and Bench fill in automatically from your Rallify raids. Raided outside Rallify? Tap an open raid to mark it Saved: ARC and the raid pickers skip that character for the instance until reset.
              </div>
              <div className="flex items-center gap-3 flex-wrap mt-3 pt-2 border-t border-[var(--c-line)]">
                {(["locked", "scheduled", "bench", "manual", "open"] as LockState[]).map(s => (
                  <span key={s} className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[var(--c-ink-3)]">
                    <span className="w-2 h-2 rounded-sm" style={{ background: STATE_META[s].accent }} />
                    {STATE_META[s].label}
                  </span>
                ))}
              </div>
              {resetLabel && (
                <div className="mt-2 text-[11px] text-amber-800/70 dark:text-amber-200/70">
                  Resets in <span className="font-bold text-amber-700 dark:text-amber-300 tabular-nums">{resetLabel}</span>
                  {resetDayLabel && ` (${resetDayLabel})`}
                </div>
              )}
              <div className="absolute bottom-full right-1.5 border-4 border-transparent border-b-black/95"></div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
